/* eslint-disable react/display-name */
import React, { memo, RefObject, useCallback, useEffect, useRef } from 'react'

import { usePrevious } from 'hooks/onboarding/usePrevious'

type Props = {
  startTime: number
  endTime: number
  loop: boolean
  src: string
}

const useVideoController = (
  start: number,
  end: number,
  loop: boolean
): RefObject<HTMLVideoElement> => {
  const ref = useRef<HTMLVideoElement>(null)

  const prevStart = usePrevious(start)

  const timeoutId = useRef<number>()

  const playUntilEnd = useCallback((startTime: number, endTime: number) => {
    const shouldBePaused =
      (!!ref.current &&
        !!ref.current.currentTime &&
        ref.current.currentTime >= endTime &&
        !ref.current.paused) ||
      (startTime === endTime &&
        ref.current &&
        ref.current.currentTime >= endTime)

    const shouldSkipAhead =
      startTime === endTime &&
      !!ref.current &&
      ref.current.currentTime < startTime

    if (shouldBePaused) {
      ref.current.pause()
      timeoutId.current && cancelAnimationFrame(timeoutId.current)
      return
    } else if (ref.current?.paused) {
      if (shouldSkipAhead) {
        ref.current.currentTime = startTime
      }
      ref.current.play()
    }
    timeoutId.current = requestAnimationFrame(() =>
      playUntilEnd(startTime, endTime)
    )
  }, [])

  const loopVideo = useCallback(
    (startTime: typeof start, endTime: typeof end) => {
      if (ref.current?.paused) {
        ref.current.play()
      }

      if (!!ref.current?.currentTime && ref.current.currentTime >= endTime) {
        ref.current.currentTime = startTime
        ref.current.play()
      }

      timeoutId.current = requestAnimationFrame(() =>
        loopVideo(startTime, endTime)
      )
    },
    []
  )

  useEffect(() => {
    timeoutId.current && cancelAnimationFrame(timeoutId.current)

    if (loop) {
      timeoutId.current = requestAnimationFrame(() => loopVideo(start, end))
    } else {
      timeoutId.current = requestAnimationFrame(() => playUntilEnd(start, end))
    }
  }, [start, end, loop, loopVideo, playUntilEnd])

  return ref
}

export const VideoSignIn = memo((props: Props) => {
  const { src, startTime, endTime, loop } = props

  const videoRef = useVideoController(startTime, endTime, loop)

  return (
    <div>
      <video
        key={src}
        width="1024"
        controls={false}
        autoPlay
        height="auto"
        muted
        loop={loop}
        ref={videoRef}
      >
        <source src={src} type="video/mp4" />
        Your browser does not support the video tag.
      </video>
    </div>
  )
})
